import { BucketFindDto, type BucketFindInterface } from '@zaoblako/shared/modules/storage/dtos/bucket/bucket-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { StorageDtoKeyEnum } from '@zaoblako/shared/modules/storage/enums/storage-dto-key.enum';

import * as yup from 'yup';

/** File descriptor response body - DTO signature */
export interface FileFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: string;

  // Tech columns
  /** File creation date and time */
  created: Date;
  /** File last update date and time */
  updated: Date;
  /** File soft delete date and time */
  deleted?: Date;

  // Data columns
  /** File extension (always starts with `.`) */
  extension: string;
  /** File name in S3 storage. This value will be generated automatically. */
  path: string;
  /** Original file name */
  name: string;
  /** File MIME type */
  mime: string;
  /** File size in bytes */
  size: number;
  /** File CRC32C checksum */
  checksum?: number;
  /** Width in pixels (for image files only) */
  imageWidth?: number;
  /** Height in pixels (for image files only) */
  imageHeight?: number;
  /**
   * Unanalyzed file flag:
   *   - File without checksum;
   *   - Image file without width or height;
   */
  unanalyzed?: boolean;

  // Relations
  /** Bucket this file belongs to */
  bucket: BucketFindInterface;
}

/** File descriptor response body - DTO schema */
export const FileFindDto = SerdeDtoClass.create<FileFindInterface>(StorageDtoKeyEnum.FILE_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    hint: 'UUID',
    serializer: yup.string(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    sortable: true,
    label: 'Изменен',
    serializer: yup.date(),
  },
  extension: {
    sortable: true,
    label: 'Расширение файла',
    hint: 'Всегда начинается с точки',
    serializer: yup.string(),
  },
  path: {
    sortable: true,
    label: 'Путь',
    hint: 'Имя файла в S3, "склееные" значения полей "Уникальный ключ файла" и "Расширение файла"',
    serializer: yup.string(),
  },
  name: {
    sortable: true,
    label: 'Оригинальное название файла',
    serializer: yup.string(),
  },
  mime: {
    label: 'MIME-тип файла',
    serializer: yup.string(),
  },
  size: {
    sortable: true,
    label: 'Размер файла в байтах',
    serializer: yup.number(),
  },
  checksum: {
    optional: true,
    label: 'Контрольная сумма файла',
    hint: 'Вычисляется по алгоритму CRC32',
    serializer: yup.number(),
  },
  imageWidth: {
    optional: true,
    label: 'Ширина изображения в пикселях',
    hint: 'Определяется только файлов изображений',
    serializer: yup.number(),
  },
  imageHeight: {
    optional: true,
    label: 'Высота изображения в пикселях',
    hint: 'Определяется только файлов изображений',
    serializer: yup.number(),
  },
  unanalyzed: {
    optional: true,
    label: 'Файл не проанализирован',
    hint: 'Не проанализированным считается файл без вычисленной контрольной суммы и, если это изображение, без разрешения',
    serializer: yup.boolean(),
  },
  bucket: {
    sortable: true,
    optional: true,
    label: '"Корзина" S3, в которой находится файл',
    nested: BucketFindDto,
    serializer: (_, nested) => nested,
  },
});
